<template>
  <div class="transaction-summary-block">
    <!-- Line items -->
    <div class="receipt-items">
      <div
        v-if="currentBooking && currentBooking.game_name"
        class="receipt-item receipt-item--current"
      >
        <div class="receipt-item__desc">
          <div class="receipt-item__name-row">
            <span class="text-white">{{ currentBooking.game_name }}</span>
            <span v-if="currentBooking.group_size" class="receipt-item__qty text-muted">x{{ currentBooking.group_size }}</span>
          </div>
          <div v-if="currentBooking.display_date || currentBooking.display_time" class="receipt-item__meta">
            <span v-if="currentBooking.display_date">{{ currentBooking.display_date }}</span>
            <span v-if="currentBooking.display_date && currentBooking.display_time"> · </span>
            <span v-if="currentBooking.display_time">{{ currentBooking.display_time }}</span>
          </div>
        </div>
        <span class="text-white">{{ getAmount(currentBooking.price) }}</span>
      </div>
      <div v-for="sib in siblingBookings" :key="'sib-' + sib.id" class="receipt-item">
        <div class="receipt-item__desc">
          <div class="receipt-item__name-row">
            <span class="text-white">{{ sib.game_name || 'Booking' }}</span>
            <span v-if="sib.group_size" class="receipt-item__qty text-muted">x{{ sib.group_size }}</span>
          </div>
          <div v-if="sib.display_date || sib.display_time" class="receipt-item__meta">
            <span v-if="sib.display_date">{{ sib.display_date }}</span>
            <span v-if="sib.display_date && sib.display_time"> · </span>
            <span v-if="sib.display_time">{{ sib.display_time }}</span>
          </div>
        </div>
        <span class="text-white">{{ getAmount(sib.price) }}</span>
      </div>
      <div v-for="item in lineItems" :key="'item-' + item.id" class="receipt-item">
        <div class="receipt-item__desc">
          <div class="receipt-item__name-row">
            <span class="text-white">{{ item.item_name }}</span>
            <span class="receipt-item__qty text-muted">x{{ item.quantity }}</span>
          </div>
        </div>
        <!-- Show the line subtotal (price * qty), not item.total — total
             already includes allocated tax/fee, which the receipt breaks out
             on dedicated rows below. Showing total here double-counts tax in
             the user's mental math against the Taxes/Fees rows. Matches the
             "$10.00" column on the Transactions detail page. -->
        <span class="text-white">{{ getAmount(item.amount != null ? item.amount : item.total) }}</span>
      </div>
      <div v-for="gc in giftCards" :key="'gc-' + gc.id" class="receipt-item">
        <div class="receipt-item__desc">
          <div class="receipt-item__name-row">
            <span class="text-white">{{ gc.gift_card_type || 'Gift card' }}</span>
            <span class="receipt-item__code text-muted">{{ formatGiftCardCode(gc.code) }}</span>
          </div>
        </div>
        <span class="text-white">{{ getAmount(gc.total) }}</span>
      </div>
    </div>

    <div class="receipt-divider"></div>

    <!-- Financial breakdown -->
    <div class="receipt-totals">
      <div class="receipt-row">
        <span class="text-white">Subtotal</span>
        <span class="text-white">{{ getAmount(displayedSubtotal) }}</span>
      </div>
      <div v-for="(t, i) in taxLines" :key="'TXN-TAX-' + i" class="receipt-row">
        <span class="receipt-row__name-group text-white">
          <span>{{ t.name }}<span v-if="t.linked_name" class="receipt-row__link"> (for {{ t.linked_name }})</span><span v-if="Number(t.pricing_type) === 1" class="receipt-row__link"> (included in subtotal)</span></span>
          <button
            v-if="canUpdate && t.id"
            class="receipt-row__delete"
            type="button"
            :disabled="isDeleting('taxes', t)"
            :aria-label="`Remove ${t.name}`"
            @click="deleteTaxLine(t)"
          >
            <i v-if="isDeleting('taxes', t)" class="fa fa-spinner fa-spin"></i>
            <i v-else class="tim-icons icon-trash-simple"></i>
          </button>
        </span>
        <span class="text-white">{{ getAmount(t.amount) }}</span>
      </div>
      <div v-if="inclusiveTaxWaived > 0" class="receipt-row">
        <span class="text-white">Included tax (waived)</span>
        <span class="text-white">-{{ getAmount(inclusiveTaxWaived) }}</span>
      </div>
      <div v-for="(f, i) in feeLines" :key="'TXN-FEE-' + i" class="receipt-row">
        <span class="receipt-row__name-group text-white">
          <span>{{ f.name }}<span v-if="f.linked_name" class="receipt-row__link"> (for {{ f.linked_name }})</span></span>
          <button
            v-if="canUpdate && f.id"
            class="receipt-row__delete"
            type="button"
            :disabled="isDeleting('fees', f)"
            :aria-label="`Remove ${f.name}`"
            @click="deleteFeeLine(f)"
          >
            <i v-if="isDeleting('fees', f)" class="fa fa-spinner fa-spin"></i>
            <i v-else class="tim-icons icon-trash-simple"></i>
          </button>
        </span>
        <span class="text-white">{{ getAmount(f.amount) }}</span>
      </div>
      <!-- Per-promo discount lines from drawer's discount_lines array. Falls
           back to the aggregated financials.discount when discount_lines is
           empty or missing (e.g., a legacy transaction that pre-dates the
           per-promo breakdown).
           Two independent v-if branches with mutually-exclusive conditions
           instead of v-if + v-else-if across <template> and <div>: that
           chain failed silently in this component (taxes/fees rendered fine
           since they have no v-else-if companion), and the aggregate path
           was firing even when discountLines had rows. -->
      <div v-if="hasDiscountLines">
        <div v-for="(d, i) in discountLines" :key="'TXN-DISC-' + i" class="receipt-row">
          <span class="receipt-row__name-group text-white">
            <span>{{ d.name }}<span v-if="d.linked_name" class="receipt-row__link"> (for {{ d.linked_name }})</span></span>
            <button
              v-if="canUpdate && d.id"
              class="receipt-row__delete"
              type="button"
              :disabled="isDeleting('discounts', d)"
              :aria-label="`Remove ${d.name}`"
              @click="deleteDiscountLine(d)"
            >
              <i v-if="isDeleting('discounts', d)" class="fa fa-spinner fa-spin"></i>
              <i v-else class="tim-icons icon-trash-simple"></i>
            </button>
          </span>
          <span class="text-white">-{{ getAmount(d.display_amount != null ? d.display_amount : d.amount) }}</span>
        </div>
      </div>
      <div v-if="!hasDiscountLines && parseFloat(financials.discount) > 0" class="receipt-row">
        <span class="text-white">Discount</span>
        <span class="text-white">-{{ getAmount(financials.discount) }}</span>
      </div>
      <!-- Per-tip rows from tipLines (with delete). Falls back to aggregate
           financials.tips when no per-tip data is available (legacy
           transactions or tip lambdas that pre-date tip_lines). -->
      <div v-if="tipLines && tipLines.length > 0">
        <div v-for="(tip, i) in tipLines" :key="'TXN-TIP-' + i" class="receipt-row">
          <span class="receipt-row__name-group text-white">
            <span>Tip<span v-if="tip.assignee_name" class="receipt-row__link"> (for {{ tip.assignee_name }})</span></span>
            <button
              v-if="canUpdate && tip.id"
              class="receipt-row__delete"
              type="button"
              :disabled="isDeleting('tips', tip)"
              aria-label="Remove tip"
              @click="deleteTipLine(tip)"
            >
              <i v-if="isDeleting('tips', tip)" class="fa fa-spinner fa-spin"></i>
              <i v-else class="tim-icons icon-trash-simple"></i>
            </button>
          </span>
          <span class="text-white">{{ getAmount(tip.amount) }}</span>
        </div>
      </div>
      <div v-else-if="parseFloat(financials.tips) > 0" class="receipt-row">
        <span class="text-white">Tips</span>
        <span class="text-white">{{ getAmount(financials.tips) }}</span>
      </div>
    </div>

    <!-- Add-actions arranged as a 2-col grid. Left column = line items
         (booking, item, gift card). Right column = adjustments (promo, tax,
         fee, tip). Expanded forms (tip, promo) appear below the grid as
         full-width sections so they don't get cramped in half-width cells. -->
    <div v-if="canUpdate && transactionId" class="receipt-adjustments">
      <div class="receipt-adjustments__grid">
        <div class="receipt-adjustments__column">
          <a
            v-if="canAddBooking"
            href="#"
            class="receipt-adjustments__add"
            @click.prevent="emit('add-booking')"
          >
            <i class="tim-icons icon-calendar-60"></i> Add another booking
          </a>
          <a
            href="#"
            class="receipt-adjustments__add"
            @click.prevent="requestAddItem"
          >
            <i class="tim-icons icon-cart"></i> Add item
          </a>
          <!-- TODO: Add gift card -->
        </div>
        <div class="receipt-adjustments__column">
          <a
            v-if="!promoExpanded"
            href="#"
            class="receipt-adjustments__add"
            @click.prevent="promoExpanded = true"
          >
            <i class="tim-icons icon-tag"></i> Add promo code
          </a>
          <!-- TODO: Add tax -->
          <!-- TODO: Add fee -->
          <a
            v-if="!tipExpanded"
            href="#"
            class="receipt-adjustments__add"
            @click.prevent="tipExpanded = true"
          >
            <i class="tim-icons icon-simple-add"></i> Add tip
          </a>
        </div>
      </div>

      <!-- Tip entry — POSTs to /transactions/{id}/tips. Full-width so the
           amount input + assignee picker have room to sit side by side. -->
      <div v-if="tipExpanded" class="receipt-adjustments__field">
        <div class="receipt-adjustments__row">
          <div class="receipt-adjustments__col receipt-adjustments__col--tip">
            <base-input label="Tip amount" class="mb-0">
              <money-input
                v-model="tipAmount"
                :currency="currency"
                :currency-symbol="currencySymbol"
                :currency-format="currencyFormat"
              />
            </base-input>
          </div>
          <div class="receipt-adjustments__col receipt-adjustments__col--assignee">
            <base-input label="Assignee (optional)" class="mb-0">
              <company-user-picker
                v-model="tipAssignee"
                :company-id="companyId"
                :company-group-id="companyGroupId"
                placeholder="Search staff..."
              />
            </base-input>
          </div>
        </div>
        <div class="receipt-adjustments__actions">
          <base-button size="sm" type="primary" :loading="tipSaving" @click="submitTip">Add</base-button>
        </div>
        <small v-if="tipError" class="receipt-adjustments__error">{{ tipError }}</small>
      </div>

      <!-- Promo entry — POSTs to /transactions/{id}/discounts. -->
      <div v-if="promoExpanded" class="receipt-adjustments__field">
        <label class="receipt-adjustments__label">Promo code</label>
        <base-input class="mb-0" :error="promoError">
          <Autocomplete
            style="width: 100%"
            v-model="promoQuery"
            :fetch-suggestions="promoSearch"
            placeholder="Search promo..."
            popper-class="booking-drawer__promo-popper"
            :trigger-on-focus="false"
            @select="handlePromoSelect"
          >
            <template #default="{ item }">
              <div>
                <span class="text-primary">{{ item.name }}</span>
                <span v-if="!hasBuyXGetYRule(item.rules)"> - </span>
                <small>
                  <template v-if="!hasBuyXGetYRule(item.rules)">
                    <span v-if="item.redemption_type === 0">{{ item.value_amount }}%</span>
                    <span v-if="item.redemption_type === 1">{{ getAmount(item.value_amount) }}</span>
                    <span v-if="item.value_type === 0"> per transaction</span>
                    <span v-if="item.value_type === 1"> per line item</span>
                    <span v-if="item.value_type === 2"> per quantity</span>
                  </template>
                  <span
                    class="ml-2"
                    style="text-transform: uppercase; line-height: 9px; color: white; background-color: #1d8cf8; border-radius: 2px; padding: 2px 5px; font-size: 9px"
                    v-if="hasBuyXGetYRule(item.rules)"
                  >buy x get y</span>
                  <span
                    class="ml-2"
                    style="text-transform: uppercase; line-height: 9px; color: white; background-color: #344675; border-radius: 2px; padding: 2px 5px; font-size: 9px"
                    v-if="item.internal_only"
                  >internal only</span>
                  <span
                    class="ml-2"
                    style="text-transform: uppercase; line-height: 9px; color: white; background-color: #00bf9a; border-radius: 2px; padding: 2px 5px; font-size: 9px"
                    v-if="hasMinValueRule(item.rules)"
                  >value rule</span>
                  <span
                    class="ml-2"
                    style="text-transform: uppercase; line-height: 9px; color: white; background-color: #ff8d72; border-radius: 2px; padding: 2px 5px; font-size: 9px"
                    v-if="hasBundlePriceRule(item.rules)"
                  >bundle price</span>
                </small>
              </div>
            </template>
            <template #append>
              <button
                class="apply-promo-append"
                type="button"
                :disabled="!selectedPromo || promoSaving"
                @click="submitPromo"
              >Apply</button>
            </template>
          </Autocomplete>
        </base-input>
      </div>
    </div>

    <div class="receipt-divider"></div>

    <!-- Payment summary -->
    <div class="receipt-totals">
      <div class="receipt-row receipt-row--bold">
        <span class="text-white">Total</span>
        <span class="text-white">{{ getAmount(financials.total) }}</span>
      </div>
      <div class="receipt-row">
        <span class="text-white">Paid</span>
        <span :class="parseFloat(financials.paid) > 0 ? 'text-success' : 'text-white'">{{ getAmount(financials.paid) }}</span>
      </div>
      <div class="receipt-row">
        <span class="text-white">Due</span>
        <span :class="parseFloat(financials.due) > 0 ? 'text-warning' : 'text-white'">{{ getAmount(financials.due) }}</span>
      </div>
      <div v-if="parseFloat(financials.refunded) > 0" class="receipt-row">
        <span class="text-white">Refunded</span>
        <span class="text-danger">{{ getAmount(financials.refunded) }}</span>
      </div>
    </div>

    <div v-if="canUpdate && transactionId" class="receipt-waive-toggles">
      <el-switch
        :value="Number(financials.waive_taxes) === 1"
        @change="onWaiveToggle('waive_taxes', $event ? 1 : 0)"
        :disabled="waiveSaving"
        active-color="#e14eca" />
      <span class="text-white ml-2">Waive taxes</span>
      <el-switch
        class="ml-4"
        :value="Number(financials.waive_fees) === 1"
        @change="onWaiveToggle('waive_fees', $event ? 1 : 0)"
        :disabled="waiveSaving"
        active-color="#e14eca" />
      <span class="text-white ml-2">Waive fees</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, getCurrentInstance, type PropType } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
// Imported by source name (not aliased) so babel-plugin-component pulls
// element-ui/lib/theme-chalk/autocomplete.css. The template uses
// <Autocomplete> directly. Pattern matches CompanyUserPicker.vue.
import { Autocomplete } from 'element-ui'
import { API_LOCATION } from '@/api-config'
import { formatCurrency } from '@/util/currencyFormat'

interface Financials {
  price?: number | string
  total?: number | string
  tax?: number | string
  fee?: number | string
  discount?: number | string
  tips?: number | string
  paid?: number | string
  due?: number | string
  refunded?: number | string
  waive_taxes?: number
  inclusive_tax_waived?: number | string
}

interface CurrentBooking {
  game_name?: string
  group_size?: number
  price?: number | string
}

interface Promo {
  id?: number
  name: string
  value_amount?: number | string
  value_type?: number
  redemption_type?: number
  internal_only?: boolean
  rules?: Array<{ rule_type?: { rule_key?: string } }>
  [key: string]: unknown
}

const props = defineProps({
  currentBooking: { type: Object as PropType<CurrentBooking | null>, default: null },
  siblingBookings: { type: Array as PropType<any[]>, default: () => [] },
  lineItems: { type: Array as PropType<any[]>, default: () => [] },
  giftCards: { type: Array as PropType<any[]>, default: () => [] },
  financials: { type: Object as PropType<Financials>, required: true },
  taxLines: { type: Array as PropType<any[]>, default: () => [] },
  feeLines: { type: Array as PropType<any[]>, default: () => [] },
  discountLines: { type: Array as PropType<any[]>, default: () => [] },
  tipLines: { type: Array as PropType<any[]>, default: () => [] },
  transactionId: { type: [String, Number] as PropType<string | number | null>, default: null },
  companyId: { type: [String, Number], required: true },
  companyGroupId: { type: [String, Number], required: true },
  currency: { type: String, default: 'USD' },
  currencySymbol: { type: String, default: '$' },
  currencyFormat: { type: Number, default: 0 },
  numberFormat: { type: Number, default: 0 },
  canUpdate: { type: Boolean, default: false },
  canAddBooking: { type: Boolean, default: false }
})

const emit = defineEmits<{
  (e: 'updated'): void
  (e: 'add-item'): void
  (e: 'add-booking'): void
}>()

const tipExpanded = ref(false)
const tipAmount = ref<number | null>(null)
const tipAssignee = ref<{ id?: number | string } | null>(null)
const tipSaving = ref(false)
const tipError = ref('')

const promoExpanded = ref(false)
const promoQuery = ref('')
const selectedPromo = ref<Promo | null>(null)
const promoSaving = ref(false)
const promoError = ref('')

// Inclusive tax handling — mirrors TransactionDetails.getSubtotalAmount /
// getInclusiveTaxes (TransactionDetails.vue:10254). DB `transaction.price`
// stores the ex-tax subtotal; the receipt's displayed Subtotal is that plus
// any inclusive tax amounts. Tax rows with pricing_type === 1 are inclusive.
const inclusiveTaxTotal = computed(() => {
  return (props.taxLines || []).reduce((sum, t) => {
    return Number(t?.pricing_type) === 1 ? sum + Number(t?.amount || 0) : sum
  }, 0)
})
// When taxes are waived on an inclusive-tax group the engine still extracts
// the inclusive component from the stored subtotal but records no tax link;
// the drawer lambda derives that amount so the receipt can add it back.
const inclusiveTaxWaived = computed(() => {
  if (Number(props.financials?.waive_taxes) !== 1) return 0
  return Number(props.financials?.inclusive_tax_waived || 0)
})
const displayedSubtotal = computed(() => {
  return Number(props.financials?.price || 0) + inclusiveTaxTotal.value + inclusiveTaxWaived.value
})

// Whether the per-promo discount breakdown should render. Used to gate the
// two mutually-exclusive discount render paths (per-line v-for vs aggregate
// fallback). Inline `discountLines && discountLines.length > 0` checks in
// the template silently failed when paired with v-else-if across the
// <template>/<div> boundary — a Vue 2 compiler quirk we hit on this
// component specifically.
const hasDiscountLines = computed(() => {
  return Array.isArray(props.discountLines) && props.discountLines.length > 0
})

async function getAuthToken() {
  const session = await Auth.currentSession()
  return session.getIdToken().getJwtToken()
}

function getAmount(value: unknown) {
  return formatCurrency(value, {
    currency: props.currency,
    currencySymbol: props.currencySymbol,
    currencyFormat: props.currencyFormat,
    numberFormat: props.numberFormat
  })
}

function formatGiftCardCode(code: string | number | null | undefined) {
  if (!code) return ''
  const str = String(code)
  if (isNaN(Number(str))) return str
  return str.match(/.{1,4}/g)?.join(' ') ?? str
}

// Mirrors TransactionDetails.vue:7998 — buy-x-get-y promos render a badge
// instead of a value-amount line.
function hasBuyXGetYRule(rules: Promo['rules']) {
  if (!rules || rules.length === 0) return false
  return rules.some((r) => r.rule_type && r.rule_type.rule_key === 'BUY_X_GET_Y')
}

function hasMinValueRule(rules: Promo['rules']) {
  if (!rules || rules.length === 0) return false
  return rules.some((r) => r.rule_type && r.rule_type.rule_key === 'MIN_VALUE')
}

function hasBundlePriceRule(rules: Promo['rules']) {
  if (!rules || rules.length === 0) return false
  return rules.some((r) => r.rule_type && r.rule_type.rule_key === 'BUNDLE_PRICE')
}

function requestAddItem() {
  emit('add-item')
}

function cancelTip() {
  tipExpanded.value = false
  tipAmount.value = null
  tipAssignee.value = null
  tipError.value = ''
}

async function submitTip() {
  const amount = parseFloat(String(tipAmount.value ?? ''))
  if (!amount || amount <= 0) {
    tipError.value = 'Enter a valid tip amount'
    return
  }
  if (!props.transactionId) return
  tipSaving.value = true
  tipError.value = ''
  try {
    const token = await getAuthToken()
    const config = { headers: { Authorization: token } }
    const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/transactions/${props.transactionId}/tips`
    const payload: { amount: number; company_user_id?: number | string } = { amount }
    if (tipAssignee.value && tipAssignee.value.id != null) {
      payload.company_user_id = tipAssignee.value.id
    }
    await axios.post(url, payload, config)
    cancelTip()
    emit('updated')
  } catch (err: any) {
    console.error('Failed to add tip:', err)
    tipError.value = (err.response && err.response.data) || 'Failed to add tip'
  } finally {
    tipSaving.value = false
  }
}

function cancelPromo() {
  promoExpanded.value = false
  promoQuery.value = ''
  selectedPromo.value = null
  promoError.value = ''
}

async function promoSearch(queryString: string, cb: (results: Promo[]) => void) {
  try {
    const token = await getAuthToken()
    const config = { headers: { Authorization: token } }
    const today = new Date()
    const mm = String(today.getMonth() + 1).padStart(2, '0')
    const dd = String(today.getDate()).padStart(2, '0')
    const purchaseDate = `${today.getFullYear()}-${mm}-${dd}`
    const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/promos?autocomplete=${encodeURIComponent(queryString)}&purchase_date=${purchaseDate}`
    const response = await axios.get(url, config)
    const results: Promo[] = (response.data.promos || [])
      .filter((p: Promo) => p && p.name)
      .map((p: Promo) => ({ ...p, value: p.name }))
    cb(results)
  } catch (err) {
    console.error('Promo search failed:', err)
    cb([])
  }
}

function handlePromoSelect(item: Promo) {
  selectedPromo.value = item
  promoQuery.value = item.name
  promoError.value = ''
}

async function submitPromo() {
  if (!selectedPromo.value) {
    promoError.value = 'Pick a promo from the list'
    return
  }
  if (!props.transactionId) return
  promoSaving.value = true
  promoError.value = ''
  try {
    const token = await getAuthToken()
    const config = { headers: { Authorization: token } }
    const today = new Date()
    const date = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`
    const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/transactions/${props.transactionId}/discounts?date=${date}`
    await axios.post(url, { promo_code: selectedPromo.value }, config)
    cancelPromo()
    emit('updated')
  } catch (err: any) {
    console.error('Failed to apply promo:', err)
    promoError.value = (err.response && err.response.data) || 'Failed to apply promo'
  } finally {
    promoSaving.value = false
  }
}

const waiveSaving = ref(false)
const notifyInstance = getCurrentInstance()
const notify = (opts: any) => notifyInstance?.proxy.$notify(opts)

// Direct-delete handlers for tax/fee/discount/tip rows. No confirm prompt
// (per the chosen UX). On success: toast + emit 'updated' so the drawer
// refetches with the new totals. URL shapes match what TransactionDetails
// uses against the same backend endpoints, so we reuse those lambdas without
// new endpoint work.
//
// Tracking via array (not Set) because Vue 2.7's ref() doesn't observe
// Set/Map .add/.delete mutations — the spinner v-if wouldn't re-render
// after the click. Reassigning .value on each change keeps reactivity.
const deletingLineIds = ref<string[]>([])

function lineKey(prefix: string, item: any) {
  return `${prefix}:${item.id}:${item.link_type || ''}`
}

function markDeleting(key: string) {
  if (!deletingLineIds.value.includes(key)) {
    deletingLineIds.value = [...deletingLineIds.value, key]
  }
}

function clearDeleting(key: string) {
  deletingLineIds.value = deletingLineIds.value.filter(k => k !== key)
}

async function deleteLine(
  endpoint: 'taxes' | 'fees' | 'discounts' | 'tips',
  item: any,
  successLabel: string
) {
  if (!props.transactionId) return
  const key = lineKey(endpoint, item)
  if (deletingLineIds.value.includes(key)) return
  markDeleting(key)
  try {
    const token = await getAuthToken()
    const config = { headers: { Authorization: token } }
    const base = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/transactions/${props.transactionId}`
    let url: string
    if (endpoint === 'tips') {
      url = `${base}/tips?id=${item.id}`
    } else if (endpoint === 'discounts') {
      // delete-discount-from-transaction: only emit link_type when it's not
      // the default 'transaction'. TX-details' deleteDiscount uses the same
      // pattern (TransactionDetails.vue:7804-7810) — sending
      // ?link_type=transaction triggers a wrong branch in the lambda's
      // delete router and the row stays put.
      url = `${base}/discounts?promo_id=${item.id}`
      if (item.link_type && item.link_type !== 'transaction') {
        url += `&link_type=${encodeURIComponent(item.link_type)}`
      }
    } else {
      // taxes / fees share the same param shape — link_type ALWAYS sent
      // (TX-details' deleteTax/deleteFee always include it).
      const typeIdParam = endpoint === 'taxes' ? `tax_id=${item.tax_id}` : `fee_id=${item.fee_id}`
      url = `${base}/${endpoint}?id=${item.id}&${typeIdParam}&item_id=${item.item_id || ''}&link_type=${encodeURIComponent(item.link_type || 'transaction')}`
    }
    await axios.delete(url, config)
    notify({ type: 'success', message: `${successLabel} removed` })
    emit('updated')
    // Keep spinner up briefly so the icon doesn't flash back to trash
    // between DELETE success and the parent's refetch removing the row.
    setTimeout(() => clearDeleting(key), 1200)
  } catch (err: any) {
    console.error(`Failed to remove ${endpoint}:`, err)
    const message = (err.response && err.response.data) || `Failed to remove ${successLabel.toLowerCase()}`
    notify({ type: 'danger', message })
    clearDeleting(key)
  }
}

function deleteTaxLine(t: any) { return deleteLine('taxes', t, 'Tax') }
function deleteFeeLine(f: any) { return deleteLine('fees', f, 'Fee') }
function deleteDiscountLine(d: any) { return deleteLine('discounts', d, 'Discount') }
function deleteTipLine(t: any) { return deleteLine('tips', t, 'Tip') }

function isDeleting(prefix: string, item: any): boolean {
  return deletingLineIds.value.includes(lineKey(prefix, item))
}
async function onWaiveToggle(field: 'waive_taxes' | 'waive_fees', newValue: 0 | 1) {
  if (!props.transactionId || waiveSaving.value) return
  waiveSaving.value = true
  const label = field === 'waive_taxes' ? 'Taxes' : 'Fees'
  try {
    const token = await getAuthToken()
    const config = { headers: { Authorization: token } }
    const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/transactions/${props.transactionId}`
    await axios.put(url, { [field]: newValue }, config)
    emit('updated')
    notify({ type: 'success', message: `${label} ${newValue ? 'waived' : 'restored'}` })
  } catch (err: any) {
    console.error('Failed to toggle waive:', err)
    const message = (err.response && err.response.data) || `Failed to update ${label.toLowerCase()}`
    notify({ type: 'danger', message })
  } finally {
    waiveSaving.value = false
  }
}
</script>

<script lang="ts">
import { Switch } from 'element-ui'
export default {
  name: 'TransactionSummaryBlock',
  components: {
    [Switch.name]: Switch
  }
}
</script>

<style lang="scss" scoped>
.transaction-summary-block {
  font-size: 13px;
}

.receipt-waive-toggles {
  display: flex;
  align-items: center;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 12px;
}

.receipt-items {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.receipt-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 4px 0;
}

.receipt-item--current {
  border-left: 2px solid #e14eca;
  padding-left: 8px;
  margin-left: -10px;
}

.receipt-item__desc {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.receipt-item__name-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
}

.receipt-item__qty {
  font-size: 12px;
  flex-shrink: 0;
}

// Date · time meta line under the booking name. Muted so the price stays
// the dominant numeric anchor on the row.
.receipt-item__meta {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.55);
  font-variant-numeric: tabular-nums;
}

.white-content .receipt-item__meta {
  color: rgba(52, 70, 117, 0.65);
}

// "· Event 1" suffix on tax/fee/discount rows. Muted inline so the
// tax/fee name + amount remain the row's anchors.
// .receipt-row__link lives in the global _receipt.scss (shared with CreateBookingView).

// .white-content .receipt-row__link lives in the global _receipt.scss (shared with CreateBookingView).

.receipt-item__code {
  font-size: 11px;
  font-family: monospace;
}

.receipt-divider {
  border-top: 1px dashed rgba(255, 255, 255, 0.15);
  margin: 10px 0;
}

.receipt-totals {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.receipt-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 0;
}

.receipt-row--bold {
  font-weight: 600;
  padding-top: 2px;
}

// Wrapper for the left side of the row: name + (optional) inline trash
// icon, so the icon sits next to the row label rather than at the row's
// far right. `justify-content: space-between` on .receipt-row keeps the
// amount pinned to the right edge.
// .receipt-row__name-group lives in the global _receipt.scss (shared with CreateBookingView).

// .receipt-row__delete lives in the global _receipt.scss (shared with CreateBookingView).

// Light-mode trash icon contrast
// .white-content .receipt-row__delete lives in the global _receipt.scss (shared with CreateBookingView).

// .receipt-adjustments lives in the global _receipt.scss (shared with CreateBookingView).

.receipt-adjustments__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 16px;
}

.receipt-adjustments__column {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

// .receipt-adjustments__add lives in the global _receipt.scss (shared with CreateBookingView).

.receipt-adjustments__field {
  margin: 4px 0;
}

// Matches the section-label pattern from CreateBookingView.vue:2049
.receipt-adjustments__label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.receipt-adjustments__row {
  display: flex;
  gap: 12px;
  align-items: flex-start;

  > .receipt-adjustments__col {
    min-width: 0;
  }

  // amount-input-group adds an outer 1px border on top of el-input-number's
  // inner ~40px (~42px total). el-autocomplete renders ~40px. Pin both to
  // 42px so the two fields render at the same visible height on desktop.
  > .receipt-adjustments__col--tip {
    flex: 0 0 30%;

    ::v-deep .amount-input-group {
      height: 42px;
    }

    ::v-deep .el-input-number,
    ::v-deep .el-input-number .el-input,
    ::v-deep .el-input-number .el-input__inner {
      height: 100%;
      line-height: 1;
    }
  }

  > .receipt-adjustments__col--assignee {
    flex: 1 1 auto;

    ::v-deep .el-autocomplete,
    ::v-deep .el-autocomplete .el-input {
      height: 42px;
    }

    ::v-deep .el-input__inner {
      height: 42px;
      line-height: 42px;
    }
  }

  // Mobile: side-by-side feels crammed in the narrow drawer. Stack the
  // two fields so each gets full width.
  @media (max-width: 640px) {
    flex-direction: column;
    gap: 10px;

    > .receipt-adjustments__col--tip,
    > .receipt-adjustments__col--assignee {
      flex: 1 1 auto;
      width: 100%;
    }
  }
}

.receipt-adjustments__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.receipt-adjustments__error {
  display: block;
  margin-top: 6px;
  color: #f5365c;
  font-size: 11px;
}

// Inline Apply button inside the autocomplete's #append slot. The wrapper's
// pink background is set by Element UI; we keep the button transparent so
// it inherits it. The disabled state used to drop text to 35% white, which
// rendered as washed-out cream on the pink fill (low contrast). Bumped
// weight + brightness so "Apply" stays legible even before a promo is
// picked, and dimmed via opacity instead of text-only fade.
.apply-promo-append {
  background: transparent;
  border: none;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  padding: 0 14px;
  white-space: nowrap;
  transition: opacity 0.15s ease;

  &:hover:not(:disabled) { opacity: 0.85; }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

// Light-mode dashed-divider tint. The block's default uses a white tint
// that disappears on light backgrounds.
.white-content .receipt-divider {
  border-top-color: rgba(0, 0, 0, 0.12);
}
</style>
